import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import type { ISelectProps } from './Select.types';
import { Icon } from '@ui-library/Icon/Icon';
import { IconName } from '@ui-library/Icon/Icon.type';

export const Select: React.FC<ISelectProps> = ({
    label,
    helperText,
    errorText,
    size = 'md',
    variant = 'default',
    startIcon,
    endIcon,
    className = '',
    containerClassName = '',
    labelClassName = '',
    required = false,
    disabled = false,
    options,
    value,
    onChange,
    placeholder = 'Select...',
    id,
    ...props
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const containerRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const listRef = useRef<HTMLDivElement>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Reset highlighted index when opening
    useEffect(() => {
        if (isOpen) {
            const selectedIndex = options.findIndex(opt => opt.value === value);
            setHighlightedIndex(selectedIndex >= 0 ? selectedIndex : 0);

            // Focus the list for keyboard interaction
            // listRef.current?.focus(); 
        }
    }, [isOpen, value, options]);

    const handleSelect = (optionValue: string | number) => {
        onChange(optionValue);
        setIsOpen(false);
        triggerRef.current?.focus();
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (disabled) return;

        switch (e.key) {
            case 'Enter':
            case ' ':
                e.preventDefault();
                if (isOpen) {
                    if (highlightedIndex >= 0 && highlightedIndex < options.length) {
                        handleSelect(options[highlightedIndex].value);
                    }
                } else {
                    setIsOpen(true);
                }
                break;
            case 'ArrowDown':
                e.preventDefault();
                if (!isOpen) {
                    setIsOpen(true);
                } else {
                    setHighlightedIndex(prev => (prev < options.length - 1 ? prev + 1 : prev));
                }
                break;
            case 'ArrowUp':
                e.preventDefault();
                if (!isOpen) {
                    setIsOpen(true);
                } else {
                    setHighlightedIndex(prev => (prev > 0 ? prev - 1 : prev));
                }
                break;
            case 'Escape':
                if (isOpen) {
                    e.preventDefault();
                    setIsOpen(false);
                    triggerRef.current?.focus();
                }
                break;
            case 'Tab':
                if (isOpen) {
                    setIsOpen(false);
                }
                break;
        }
    };

    // Size mapping (copied from Input.tsx to ensure match)
    const sizeMapper = {
        'sm': {
            input: 'px-bg-v2-sm py-bg-v2-xs text-bg-v2-sm',
            withStartIcon: 'pl-bg-v2-4xl',
            withEndIcon: 'pr-bg-v2-4xl',
            icon: 'w-4 h-4',
            iconPosition: 'left-bg-v2-sm right-bg-v2-sm',
            option: 'px-bg-v2-sm py-bg-v2-xs text-bg-v2-sm',
        },
        'md': {
            input: 'px-bg-v2-lg py-bg-v2-md text-bg-v2-md',
            withStartIcon: 'pl-bg-v2-5xl',
            withEndIcon: 'pr-bg-v2-5xl',
            icon: 'w-5 h-5',
            iconPosition: 'left-bg-v2-lg right-bg-v2-lg',
            option: 'px-bg-v2-lg py-bg-v2-md text-bg-v2-md',
        },
        'lg': {
            input: 'px-bg-v2-lg py-bg-v2-md text-bg-v2-lg',
            withStartIcon: 'pl-bg-v2-6xl',
            withEndIcon: 'pr-bg-v2-6xl',
            icon: 'w-6 h-6',
            iconPosition: 'left-bg-v2-lg right-bg-v2-lg',
            option: 'px-bg-v2-lg py-bg-v2-md text-bg-v2-lg',
        },
    };

    // Variant mapping (copied from Input.tsx)
    const variantMapper = {
        'default': 'border-border-primary text-text-primary bg-bg-primary focus:border-border-brand  placeholder:text-text-placeholder focus:border-2',
        'error': 'border-border-error text-text-primary bg-bg-primary focus:border-border-error placeholder:text-text-placeholder focus:border-2',
        'success': 'border-border-brand text-text-primary bg-bg-primary focus:border-border-brand placeholder:text-text-placeholder focus:border-2',
        'regular': 'border-border-primary text-text-primary bg-bg-primary focus:border-border-brand placeholder:text-text-placeholder focus:border-2',
    };

    const actualVariant = errorText ? 'error' : variant;
    const selectId = id || `select-${Math.random().toString(36).substr(2, 9)}`;

    const selectedOption = options.find(opt => opt.value === value);

    const triggerClasses = `
    w-full
    flex items-center justify-between
    ${sizeMapper[size].input}
    ${startIcon ? sizeMapper[size].withStartIcon : ''}
    ${endIcon ? sizeMapper[size].withEndIcon : ''}
    ${variantMapper[actualVariant]}
    border
    rounded-bg-v2-md
    transition-all
    transition-colors
    duration-200
    focus:outline-none
    cursor-pointer
    ${disabled ? 'bg-bg-disabled text-text-disabled border-border-disabled cursor-not-allowed' : 'hover:bg-bg-primary'}
    ${className}
  `.trim().replace(/\s+/g, ' ');

    return (
        <div className={`flex flex-col gap-bg-v2-sm w-full relative ${containerClassName}`} ref={containerRef}>
            {/* Label */}
            {label && (
                <label
                    htmlFor={selectId}
                    className={`font-bg-v2-sans font-bg-v2-medium text-bg-v2-sm text-text-secondary ${labelClassName}`}
                >
                    {label}
                </label>
            )}

            {/* Trigger Container */}
            <div className="relative w-full">
                {/* Start Icon */}
                {startIcon && (
                    <div className={`absolute ${size === 'sm' ? 'left-bg-v2-sm' : 'left-bg-v2-lg'} top-1/2 -translate-y-1/2 ${sizeMapper[size].icon} text-text-quaternary pointer-events-none z-10`}>
                        {startIcon}
                    </div>
                )}

                {/* Trigger Button */}
                <button
                    ref={triggerRef}
                    id={selectId}
                    type="button"
                    className={triggerClasses}
                    onClick={() => !disabled && setIsOpen(!isOpen)}
                    onKeyDown={handleKeyDown}
                    disabled={disabled}
                    aria-haspopup="listbox"
                    aria-expanded={isOpen}
                    aria-labelledby={label ? undefined : selectId}
                    {...props}
                >
                    <span className={`font-bg-v2-sans font-bg-v2-regular truncate ${!selectedOption ? 'text-text-placeholder' : ''}`}>
                        {selectedOption ? selectedOption.label : placeholder}
                    </span>
                    <div className={`flex items-center ${sizeMapper[size].icon} text-text-tertiary transition-transform duration-200 ${isOpen ? 'transform rotate-180' : ''}`}>
                        {endIcon || <ChevronDown className="w-full h-full" />}
                    </div>
                </button>

                {/* Error Icon */}
                {errorText && (
                    <div className={`absolute ${size === 'sm' ? 'right-bg-v2-sm' : 'right-bg-v2-lg'} top-1/2 -translate-y-1/2 ${sizeMapper[size].icon} text-text-error-primary pointer-events-none mr-6 z-10`}>
                        <Icon name={IconName.Alert} size="md" />
                    </div>
                )}

                {/* Inside the trigger container, not beside it: the list is
                    `absolute top-full`, so its positioning parent decides what
                    "below" means. Anchored to the outer column it opened below
                    the helper text, leaving the description stranded between the
                    field and its options. Anchored here it opens flush under the
                    trigger and draws over the description, which is what
                    MultiSelect already did. */}
                {isOpen && !disabled && (
                    <div
                        ref={listRef}
                        className="absolute top-full left-0 mt-1 w-full bg-bg-primary rounded-bg-v2-lg shadow-bg-v2-lg border border-border-primary py-1 z-20 animate-fade-in-down max-h-60 overflow-auto scrollbar-hide"
                        role="listbox"
                    >
                        {options.map((option, index) => (
                            <div
                                key={option.value}
                                role="option"
                                aria-selected={value === option.value}
                                className={`
                    w-full text-left ${sizeMapper[size].option} font-bg-v2-sans font-bg-v2-regular transition-colors cursor-pointer
                    ${highlightedIndex === index ? 'bg-bg-secondary' : ''}
                    ${value === option.value ? 'bg-bg-secondary-subtle text-text-primary font-bg-v2-medium' : 'text-text-primary'}
                  `}
                                onClick={() => handleSelect(option.value)}
                                onMouseEnter={() => setHighlightedIndex(index)}
                            >
                                {option.label}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Error Text */}
            {errorText && (
                <span className="font-bg-v2-sans font-bg-v2-regular text-bg-v2-sm text-text-error-primary">
                    {errorText}
                </span>
            )}

            {/* Helper Text */}
            {helperText && !errorText && (
                <span className="font-bg-v2-sans font-bg-v2-regular text-bg-v2-sm text-text-tertiary">
                    {helperText}
                </span>
            )}
        </div>
    );
};
